import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const CubsSection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  // Overview content
  const overviewContent = {
    intro: {
      en: "The Cubs section is designed for boys aged 5 to 10 years, focusing on character development, physical fitness, and basic scouting skills through fun and adventure.",
      ta: "கப்ஸ் பிரிவு 5 முதல் 10 வயது வரையிலான சிறுவர்களுக்காக வடிவமைக்கப்பட்டுள்ளது, விளையாட்டு மற்றும் சாகசங்கள் மூலம் குணாதிசய வளர்ச்சி, உடல் தகுதி மற்றும் அடிப்படை சாரணர் திறன்களில் கவனம் செலுத்துகிறது."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "5+ to 10 years", ta: "5+ முதல் 10 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Cub Pack", ta: "கப் பேக்" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Do Your Best", ta: "சிறப்பாக செய்யுங்கள்" },
        icon: Target
      }
    ],
    objectives: [
      {
        title: { en: "Character Building", ta: "குணாதிசய வளர்ச்சி" },
        content: { 
          en: "Development of moral values, leadership qualities, and good citizenship",
          ta: "நன்னெறி மதிப்புகள், தலைமைப் பண்புகள் மற்றும் நல்ல குடிமகன் வளர்ச்சி"
        }
      },
      {
        title: { en: "Physical Development", ta: "உடல் வளர்ச்சி" },
        content: {
          en: "Regular exercises, outdoor games, and activities for physical fitness",
          ta: "உடல் தகுதிக்கான வழக்கமான உடற்பயிற்சிகள், வெளிப்புற விளையாட்டுகள் மற்றும் செயல்பாடுகள்"
        }
      },
      {
        title: { en: "Mental Development", ta: "மன வளர்ச்சி" },
        content: {
          en: "Learning through observation, nature study, and creative activities",
          ta: "கவனிப்பு, இயற்கை ஆய்வு மற்றும் படைப்பாற்றல் செயல்பாடுகள் மூலம் கற்றல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Cubs Section", ta: "கப்ஸ் பிரிவு" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    proficiencyBadges: {
      title: { en: "Proficiency Badges", ta: "திறன் பதக்கங்கள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Pravesh", ta: "பிரவேஷ்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Tell the first jungle story", ta: "முதல் காட்டு கதையைச் சொல்லுதல்" },
        { en: "Understand Cub Law, Motto, Promise and Greeting", ta: "கப் சட்டம், குறிக்கோள், வாக்குறுதி மற்றும் வாழ்த்து புரிந்து கொள்ளுதல்" },
        { en: "Demonstrate Cub Salute and left handshake", ta: "கப் வணக்கம் மற்றும் இடது கை குலுக்கல் காட்டுதல்" },
        { en: "Offer daily prayers", ta: "தினசரி பிரார்த்தனை செய்தல்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Pratham Charan", ta: "பிரதம் சரண்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Keep personal effects neat and tidy", ta: "தனிப்பட்ட பொருட்களை சுத்தமாக வைத்திருத்தல்" },
        { en: "Daily good turn at home and school", ta: "வீடு மற்றும் பள்ளியில் தினசரி நல்ல செயல்" },
        { en: "Practice physical activities and exercises", ta: "உடற்பயிற்சிகள் மற்றும் விளையாட்டுகள்" },
        { en: "Learn basic knots and their uses", ta: "அடிப்படை முடிச்சுகள் மற்றும் அவற்றின் பயன்பாடுகள்" }
      ],
      icon: Star
    },
    {
      title: { en: "Dwitiya Charan", ta: "த்விதீய சரண்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Learn about community and locality", ta: "சமூகம் மற்றும் பகுதி பற்றி கற்றுக்கொள்ளுதல்" },
        { en: "Prepare handicraft from waste material", ta: "கழிவுப் பொருட்களில் கைவினைப் பொருள் தயாரித்தல்" },
        { en: "Basic first aid for cuts and burns", ta: "வெட்டு மற்றும் தீக்காயங்களுக்கான அடிப்படை முதலுதவி" },
        { en: "Participate in conservation activities", ta: "பாதுகாப்பு நடவடிக்கைகளில் பங்கேற்றல்" }
      ],
      icon: Map
    },
    {
      title: { en: "Tritiya Charan", ta: "திரிதீய சரண்" },
      duration: { en: "9 months", ta: "9 மாதங்கள்" },
      requirements: [
        { en: "Make handicraft for differently abled", ta: "மாற்றுத்திறனாளிகளுக்கான கைவினைப் பொருள்" },
        { en: "Learn compass points and navigation", ta: "திசைகாட்டி புள்ளிகள் மற்றும் வழிசெலுத்தல்" },
        { en: "Advanced first aid skills", ta: "மேம்பட்ட முதலுதவி திறன்கள்" },
        { en: "Complete proficiency badge", ta: "திறன் பதக்கம் பெறுதல்" }
      ],
      icon: Compass
    },
    {
      title: { en: "Chaturtha Charan", ta: "சதுர்த சரண்" },
      duration: { en: "9 months", ta: "9 மாதங்கள்" },
      requirements: [
        { en: "Care for pet animal or bird", ta: "செல்லப்பிராணி அல்லது பறவை பராமரிப்பு" },
        { en: "Participate in overnight camp", ta: "இரவு முகாமில் பங்கேற்றல்" },
        { en: "Advanced knots and their uses", ta: "மேம்பட்ட முடிச்சுகள் மற்றும் பயன்பாடுகள்" },
        { en: "Complete special proficiency badge", ta: "சிறப்பு திறன் பதக்கம் பெறுதல்" }
      ],
      icon: Medal
    },
    {
      title: { en: "Golden Arrow", ta: "பொன் அம்பு" },
      special: true,
      requirements: [
        { en: "Complete all Charan levels", ta: "அனைத்து சரண் நிலைகளையும் நிறைவு செய்தல்" },
        { en: "Earn six proficiency badges", ta: "ஆறு திறன் பதக்கங்கள் பெறுதல்" },
        { en: "Complete before age ten", ta: "பத்து வயதிற்குள் நிறைவு செய்தல்" }
      ],
      icon: Award
    }
  ];

  const proficiencyBadges = [
    {
      category: { en: "Basic Skills", ta: "அடிப்படை திறன்கள்" },
      badges: [
        { name: { en: "Observer", ta: "கவனிப்பாளர்" }, icon: Flag },
        { name: { en: "Team Player", ta: "அணி வீரர்" }, icon: Users },
        { name: { en: "Home Craft", ta: "வீட்டு கைவினை" }, icon: Heart }
      ]
    },
    {
      category: { en: "Outdoor Skills", ta: "வெளிப்புற திறன்கள்" },
      badges: [
        { name: { en: "World Conservation", ta: "உலக பாதுகாப்பு" }, icon: Map },
        { name: { en: "First Aider", ta: "முதலுதவியாளர்" }, icon: Heart },
        { name: { en: "Cyclist", ta: "சைக்கிள் ஓட்டி" }, icon: Target }
      ]
    },
    {
      category: { en: "Special Skills", ta: "சிறப்பு திறன்கள்" },
      badges: [
        { name: { en: "Heritage", ta: "பாரம்பரியம்" }, icon: BookOpen },
        { name: { en: "Free Being Me", ta: "சுதந்திரமான நான்" }, icon: Star },
        { name: { en: "Computer Awareness", ta: "கணினி விழிப்புணர்வு" }, icon: Info }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#FFB94E] to-[#ebda98] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">{sectionTitles.overview.title[isTamil ? 'ta' : 'en']}</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#FFB94E] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#FFB94E] bg-opacity-10 rounded-lg text-[#FFB94E]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>

       {/* Objectives */}
       <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Advancement Section */}
        <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#FFB94E] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${level.special ? 'border-2 border-yellow-500' : ''}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${level.special ? 'bg-yellow-50 text-yellow-600' : 'bg-[#FFB94E] bg-opacity-10 text-[#FFB94E]'}`}>
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    {level.duration && (
                      <p className="text-sm text-gray-500">
                        {level.duration[isTamil ? 'ta' : 'en']}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proficiency Badges Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#FFB94E] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {proficiencyBadges.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.badges.map((badge, bIndex) => (
                    <div key={bIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#FFB94E] bg-opacity-10 rounded-lg text-[#FFB94E]">
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {badge.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CubsSection;